import { useRef, useState, useCallback, type ReactNode, type TouchEvent } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

const SWIPE_THRESHOLD = 80;
const MAX_SWIPE = 120;

interface SwipeableNotificationProps {
  children: ReactNode;
  onSwipeComplete: () => void;
  enabled: boolean;
  showHint?: boolean;
}

export function SwipeableNotification({
  children,
  onSwipeComplete,
  enabled,
  showHint = false,
}: SwipeableNotificationProps) {
  const [offsetX, setOffsetX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [thresholdHit, setThresholdHit] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const locked = useRef(false);
  const dismissed = useRef(false);
  const thresholdTriggered = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || dismissed.current || completing) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    locked.current = false;
    thresholdTriggered.current = false;
    setThresholdHit(false);
    setIsSwiping(false);
  }, [completing, enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || dismissed.current || completing) return;

    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;

    if (!locked.current && !isSwiping) {
      if (Math.abs(dy) > 10) return;
      if (Math.abs(dx) > 10) {
        locked.current = true;
        setIsSwiping(true);
      } else {
        return;
      }
    }

    if (!locked.current) return;

    const clamped = Math.max(0, Math.min(dx, MAX_SWIPE));
    setOffsetX(clamped);

    // Trigger haptic feedback when crossing threshold
    if (clamped >= SWIPE_THRESHOLD && !thresholdTriggered.current) {
      thresholdTriggered.current = true;
      setThresholdHit(true);
      navigator.vibrate?.(10);
      setTimeout(() => setThresholdHit(false), 300);
    } else if (clamped < SWIPE_THRESHOLD) {
      thresholdTriggered.current = false;
    }
  }, [completing, enabled, isSwiping]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !locked.current || dismissed.current || completing) {
      setOffsetX(0);
      setIsSwiping(false);
      return;
    }

    if (offsetX >= SWIPE_THRESHOLD) {
      dismissed.current = true;
      setCompleting(true);
      onSwipeComplete();
      setOffsetX(0);
    } else {
      setOffsetX(0);
    }

    setIsSwiping(false);
  }, [completing, enabled, offsetX, onSwipeComplete]);

  const progress = Math.min(offsetX / SWIPE_THRESHOLD, 1);
  const showSwipeHint = showHint && offsetX === 0 && !dismissed.current;

  return (
    <div
      className={cn(
        "relative overflow-hidden transition-all",
        thresholdHit && "animate-[shake_0.3s_ease-in-out]"
      )}
      style={{
        touchAction: "pan-y",
      }}
    >
      {/* Background revealed on swipe */}
      {enabled && (
        <div
          className="absolute inset-0 z-0 flex items-center pl-4 transition-colors"
          style={{
            backgroundColor: `hsl(142 71% 45% / ${progress * 0.25})`,
          }}
        >
          <Check
            className="w-5 h-5 transition-opacity"
            style={{
              opacity: progress,
              color: `hsl(142 71% 45%)`,
            }}
          />
        </div>
      )}

      {/* Foreground content */}
      <div
        className={cn(
          "relative z-20 bg-background",
          !isSwiping && offsetX === 0 && "transition-transform duration-200"
        )}
        style={{
          transform: enabled ? `translateX(${offsetX}px)` : undefined,
          transition: isSwiping ? "none" : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>

      {showSwipeHint && (
        <p className="px-3 pb-1 pt-0.5 text-[11px] text-muted-foreground animate-pulse pointer-events-none md:hidden">
          Deslize para a direita para marcar como lida →
        </p>
      )}
    </div>
  );
}
